import { CalendlyExpert } from '@/features/experts/types/CalendlyExpert';
import { ExpertRole } from '@/features/experts/types/ExpertRole';
import { useCallback, useState } from 'react';
import { getMedicalUserByRole, getNutritionistByPatientId, getPhysicianByPatientId } from '../../../../../utils/apis/medicaluser-service';
import { useAuthStore } from '../../../../../utils/stores/useAuthStore';
import { useExpertsStore } from '../../../../../utils/stores/useExpertsStore';
import { useAutoAssignExperts } from './useAutoAssignExperts';

interface UseFetchExpertsOnLoginReturn {
  fetchExpertsOnLogin: () => Promise<void>;
  isFetching: boolean;
  error: string | null;
}

export const useFetchExpertsOnLogin = (): UseFetchExpertsOnLoginReturn => {
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { auth } = useAuthStore();
  const { updateExperts } = useExpertsStore();
  const { autoAssignMissingExperts } = useAutoAssignExperts();

  const fetchExpertsOnLogin = useCallback(async () => {
    if (!auth.userId || !auth.idToken) {
      setError('User not authenticated');
      return;
    }

    setIsFetching(true);
    setError(null);

    try {
      // First, try to fetch existing assignments
      const [nutritionist, physician, adminExperts] = await Promise.allSettled([
        getNutritionistByPatientId(auth.idToken, auth.userId),
        getPhysicianByPatientId(auth.idToken, auth.userId),
        getMedicalUserByRole(auth.idToken, ExpertRole.Admin)
      ]);

      // Find specific admin from the array (same logic as useExpertsByRole)
      let admin: CalendlyExpert | null = null;
      if (adminExperts.status === 'fulfilled' && adminExperts.value && adminExperts.value.length > 0) {
        admin = adminExperts.value.find((e) =>
          ["admin@holifya.com", "tommaso.marchi@holifya.com"].includes(e.email)
        ) || null;
      }

      // Update store with existing assignments
      updateExperts({
        nutritionist: nutritionist.status === 'fulfilled' ? nutritionist.value : null,
        physician: physician.status === 'fulfilled' ? physician.value : null,
        specialist: null,
        admin: admin,
      });

      // Then auto-assign any missing experts
      await autoAssignMissingExperts();

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch and assign experts';
      setError(errorMessage);
      console.error('Error in fetchExpertsOnLogin:', err);
    } finally {
      setIsFetching(false);
    }
  }, [auth.userId, auth.idToken, updateExperts, autoAssignMissingExperts]);

  return {
    fetchExpertsOnLogin,
    isFetching,
    error,
  };
}; 